<template>
  <div class="rules vld-parent">
    <loading
      :active.sync="loading"
      :can-cancel="false"
      color="#2196f3"
      :z-index="9"
      :is-full-page="fullPage"
    ></loading>
    <notifications group="rules" position="top center" />
    <div class="card">
      <div
        class="d-flex justify-content-between align-items-center card-header"
      >
        <div class="d-flex align-items-center gap-2">
          <h3 class="mb-0">IP Profiles</h3>
          <TextInput
            class="mx-4 my-0"
            placeholder="Enter search query..."
            v-model="query"
          />
        </div>
        <div
          class="d-flex align-items-center"
          v-if="currentUser.role === 'SuperAdmin'"
        >
          <div class="position-relative mr-4 font-weight-bold">
            <span class="custom-input-label">
              <i class="mr-2 text-light fa fa-file-import"></i>
              Import
            </span>
            <b-form-file
              v-model="file"
              accept=".json"
              @input="readFile"
              class="btn btn-info"
            ></b-form-file>
          </div>
          <button
            @click.prevent="openCreateIPModal"
            class="font-weight-bold btn btn-primary"
          >
            <i class="text-light fa fa-external-link-square-alt"></i>
            Create
          </button>
        </div>
      </div>
      <div class="card-body">
        <Table :fields="fields" :data="filteredData">
          <template #ips="row">
            <Ips :ips="row.item.ips" :id="row.item.id" />
          </template>
          <template #offer-codecs="row">
            <OfferCodecs :offer_codecs="row.item.offer_codecs" />
          </template>
          <template #details="row">
            <Details v-bind="row.item" />
          </template>
          <template #actions="row">
            <div class="d-flex">
              <router-link
                class="p-0 text-primary btn btn-link"
                :to="{
                  name: 'ip-rules',
                  params: { id: row.item.id },
                }"
              >
                <i class="text-primary fa fa-external-link-alt"></i>
                Rules
              </router-link>
              <button
                v-if="currentUser.role === 'SuperAdmin'"
                class="ml-4 p-0 text-primary btn btn-link"
                @click.prevent="openEditIPModal(row)"
              >
                <i class="text-primary fa fa-pen"></i>
              </button>
              <button
                class="ml-4 p-0 text-info btn btn-link"
                @click="downloadBackup(row.item.id, row.item.name)"
              >
                <i class="text-info fa fa-cloud-download-alt"></i>
              </button>
              <button
                v-if="currentUser.role === 'SuperAdmin'"
                class="ml-4 p-0 text-danger btn btn-link"
                @click="openDeletePopup(row.item.id)"
              >
                <i class="text-danger fa fa-trash"></i>
              </button>
            </div>
          </template>
        </Table>
      </div>
    </div>

    <modal name="createIPModal" :width="500" height="auto" @closed="clearData">
      <div class="pt-10 pb-5 container vld-parent">
        <loading
          :active.sync="createLoading"
          :can-cancel="false"
          color="#2196f3"
          :z-index="9"
          :is-full-page="fullPage"
        ></loading>
        <h2 class="text-center">Create IP</h2>
        <form>
          <b-form-group label="Name">
            <b-form-input required v-model="form.name" type="text">
            </b-form-input>
            <span v-if="error.name" class="text-danger small">{{
              error.name
            }}</span>
          </b-form-group>

          <b-form-group label="IPs">
            <FieldAdder
              placeholder="127.0.0.1"
              v-for="(ip, idx) in form.ips"
              :key="idx"
              :last="form.ips.length - 1 === idx"
              v-model="form.ips[idx]"
              @remove="form.ips.splice(idx, 1)"
            />
            <div class="action">
              <b-button block variant="primary" @click="form.ips.push('')">
                <i class="text-light fa fa-plus" />
              </b-button>
            </div>
            <span v-if="error.ips" class="text-danger small">{{
              error.ips
            }}</span>
          </b-form-group>
          <b-form-group label="Prefix">
            <b-form-input v-model="form.prefix" type="text"> </b-form-input>
            <span v-if="error.prefix" class="text-danger small">{{
              error.prefix
            }}</span>
          </b-form-group>
          <div class="row">
            <b-form-group class="col-6" label="SIP Server">
              <b-form-input v-model="form.sip_server" type="text">
              </b-form-input>
              <span v-if="error.sip_server" class="text-danger small">{{
                error.sip_server
              }}</span>
            </b-form-group>
            <b-form-group class="col-6" label="SIP Server Port">
              <b-form-input v-model="form.sip_server_port" type="text">
              </b-form-input>
              <span v-if="error.sip_server_port" class="text-danger small">{{
                error.sip_server_port
              }}</span>
            </b-form-group>
          </div>
          <div class="row">
            <b-form-group class="col-6" label="SIP Server Transport">
              <b-form-select
                v-model="form.sip_server_transport"
                :options="sipServerTransportOptions"
              >
                <template #first>
                  <b-form-select-option value="" disabled>
                    select sip server
                  </b-form-select-option>
                </template>
              </b-form-select>
              <span
                v-if="error.sip_server_transport"
                class="text-danger small"
                >{{ error.sip_server_transport }}</span
              >
            </b-form-group>
            <b-form-group class="col-6" label="RTP Proxy">
              <b-form-select v-model="form.rtp_proxy" :options="yesNoOptions">
                <template #first>
                  <b-form-select-option value="" disabled>
                    Yes | No
                  </b-form-select-option>
                </template>
              </b-form-select>

              <span v-if="error.rtp_proxy" class="text-danger small">{{
                error.rtp_proxy
              }}</span>
            </b-form-group>
          </div>
          <b-form-group label="Offer Codecs">
            <multiselect
              v-model="form.offer_codecs"
              multiple
              hide-selected
              :options="offerCodecsOptions"
              :close-on-select="false"
              :option-height="40"
              :show-labels="false"
            >
              <template slot="option" slot-scope="props">
                <span class="option__title">{{ props.option }}</span>
              </template>
            </multiselect>
            <span v-if="error.offer_codecs" class="text-danger small">{{
              error.offer_codecs
            }}</span>
          </b-form-group>
          <div class="text-center">
            <button
              @click.prevent="createIP"
              class="font-weight-bold btn btn-primary"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </modal>

    <modal name="editIpModal" :width="500" height="auto" @closed="clearData">
      <loading
        :active.sync="editLoading"
        :can-cancel="false"
        color="#5739B2"
        :z-index="9"
        :is-full-page="fullPage"
      ></loading>
      <div class="d-flex flex-column align-items-center py-10 container">
        <h2 class="text-center">Edit IP Profile</h2>
        <b-badge variant="info" v-if="editForm.has_rules">HAS RULES</b-badge>
        <b-badge variant="warning" v-else>NO RULES</b-badge>

        <form>
          <b-form-group class="text-left" label="Name">
            <b-form-input required v-model="editForm.name" type="text">
            </b-form-input>
            <span v-if="editError.name" class="text-danger small">{{
              editError.name
            }}</span>
          </b-form-group>
          <b-form-group class="text-left" label="IPs">
            <FieldAdder
              placeholder="127.0.0.1"
              v-for="(ip, idx) in editForm.ips"
              :key="idx"
              v-model="editForm.ips[idx]"
              @remove="deleteSubnet(idx)"
            />
            <div class="action">
              <b-button block variant="primary" @click="editForm.ips.push('')">
                <i class="text-light fa fa-plus" />
              </b-button>
            </div>
            <span v-if="editError.ips" class="text-danger small">{{
              editError.ips
            }}</span>
          </b-form-group>

          <b-form-group class="text-left" label="Prefix">
            <b-form-input v-model="editForm.prefix" type="text"> </b-form-input>
            <span v-if="editError.prefix" class="text-danger small">{{
              editError.prefix
            }}</span>
          </b-form-group>
          <div class="row">
            <b-form-group class="text-left col-6" label="SIP Server">
              <b-form-input v-model="editForm.sip_server" type="text">
              </b-form-input>
              <span v-if="editError.sip_server" class="text-danger small">{{
                editError.sip_server
              }}</span>
            </b-form-group>
            <b-form-group class="text-left col-6" label="SIP Server Port">
              <b-form-input v-model="editForm.sip_server_port" type="text">
              </b-form-input>
              <span
                v-if="editError.sip_server_port"
                class="text-danger small"
                >{{ editError.sip_server_port }}</span
              >
            </b-form-group>
          </div>
          <div class="row">
            <b-form-group class="text-left col-6" label="SIP Server Transport">
              <b-form-select
                v-model="editForm.sip_server_transport"
                :options="sipServerTransportOptions"
              >
                <template #first>
                  <b-form-select-option value="" disabled>
                    select sip server
                  </b-form-select-option>
                </template>
              </b-form-select>
              <span
                v-if="editError.sip_server_transport"
                class="text-danger small"
                >{{ editError.sip_server_transport }}</span
              >
            </b-form-group>
            <b-form-group class="text-left col-6" label="RTP Proxy">
              <b-form-select
                v-model="editForm.rtp_proxy"
                :options="yesNoOptions"
              >
                <template #first>
                  <b-form-select-option value="" disabled>
                    Yes | No
                  </b-form-select-option>
                </template>
              </b-form-select>

              <span v-if="editError.rtp_proxy" class="text-danger small">{{
                editError.rtp_proxy
              }}</span>
            </b-form-group>
          </div>
          <b-form-group class="text-left" label="Offer Codecs">
            <multiselect
              v-model="editForm.offer_codecs"
              multiple
              hide-selected
              :options="offerCodecsOptions"
              :close-on-select="false"
              :option-height="40"
              :show-labels="false"
            >
              <template slot="option" slot-scope="props">
                <span class="option__title">{{ props.option }}</span>
              </template>
            </multiselect>
            <span v-if="editError.offer_codecs" class="text-danger small">{{
              editError.offer_codecs
            }}</span>
          </b-form-group>
          <div class="text-center">
            <button
              @click.prevent="editIP"
              class="font-weight-bold btn btn-primary"
            >
              Edit
            </button>
          </div>
        </form>
      </div>
    </modal>

    <modal name="deletePopup" :width="500" height="auto" @closed="clearData">
      <div class="pt-10 pb-5 container vld-parent">
        <loading
          :active.sync="deleteLoading"
          :can-cancel="false"
          color="#5739B2"
          :z-index="9"
          :is-full-page="fullPage"
        ></loading>
        <h1 class="mb-5 text-center color-primary">Delete IP</h1>
        <form
          class="text-center delete-banner__form"
          @submit.prevent="deleteHandler"
          id="delete-banner"
        >
          <p>Are you sure you want to delete this IP?</p>
          <div class="justify-content-center mt-5 row">
            <button class="rounded w-20 btn btn-danger">Delete</button>
          </div>
        </form>
      </div>
    </modal>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import Vue from "vue";
import IP from "./components/rule-ip";
Vue.component("rule-ip", IP);
import http from "@/axios";
import TextInput from "@/components/TextInput.vue";
import Vuetable from "vuetable-2/src/components/Vuetable";
import DataTable from "@/components/DataTable.vue";
import FieldAdder from "@/components/FieldAdder.vue";
import FileSaver from "file-saver";
import Table from "@/components/Table.vue";
import Details from "./components/details.vue";
import OfferCodecs from "./components/offerCodecs.vue";
import Ips from "./components/ips.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    TextInput,
    Vuetable,
    DataTable,
    FieldAdder,
    Table,
    Details,
    OfferCodecs,
    Ips,
  },
  data() {
    return {
      query: "",
      error: {},
      form: {
        ips: [""],
        offer_codecs: [],
      },
      editForm: {
        ips: [""],
        offer_codecs: [],
      },
      editError: {},
      rules: [],
      ips: [""],
      file: null,
      loading: false,
      createLoading: false,
      editLoading: false,
      deleteLoading: false,
      currentIPID: null,
      fullPage: false,
      importedJSON: "",
      fields: [
        "idx",
        "name",
        { key: "ips", label: "IPs" },
        "sip_server",
        { key: "offer-codecs", label: "Offer Codecs" },
        { key: "details", label: "Details" },
        "actions",
      ],
      sipServerTransportOptions: [
        { text: "UDP", value: "udp" },
        { text: "TCP", value: "tcp" },
        { text: "TLS", value: "tls" },
        { text: "WS", value: "ws" },
        { text: "WSS", value: "wss" },
      ],
      yesNoOptions: [
        { text: "Yes", value: "yes" },
        { text: "No", value: "no" },
      ],
      offerCodecsOptions: ["PCMU", "PCMA", "GSM", "G729"],
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    filteredData() {
      return this.rules.filter(
        (rule) =>
          rule.name.toLowerCase().includes(this.query) ||
          rule.ips
            .map((ipData) => ipData.ip)
            .join(",")
            .includes(this.query)
      );
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "IP Profiles" }]);
    this.fetchRules();
  },
  methods: {
    // Emits
    openCreateIPModal() {
      this.$modal.show("createIPModal");
    },
    openEditIPModal(row) {
      this.ips = row.item.ips;
      const ips = row.item.ips.map(({ ip }) => ip);
      this.editForm = { ...row.item, ips };
      this.$modal.show("editIpModal");
    },
    openDeletePopup(id) {
      this.currentIPID = id;
      this.$modal.show("deletePopup");
    },
    // Utils
    clearData() {
      this.form = { ips: [""] };
      this.currentIPID = null;
    },
    readFile(file) {
      let reader = new FileReader();
      reader.onload = (e) => {
        this.importedJSON = e.target.result;
        this.uploadFile();
        let json = JSON.parse(e.target.result);
      };
      reader.readAsText(file);
    },
    // Api
    async downloadBackup(id, name) {
      this.loading = true;
      await http
        .get(`/sbc/ips/backup/${id}`)
        .then((res) => {
          this.loading = false;
          let response = res.data.data;
          var blob = new Blob([JSON.stringify(response, null, "\t")], {
            type: "application/json;charset=utf-8",
          });
          FileSaver.saveAs(blob, name + ".json");

          this.$notify({
            group: "datatable",
            title: res.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          this.$notify({
            group: "datatable",
            title: "Something went wrong!",
            type: "error",
          });
          this.loading = false;
        });
    },
    async fetchRules() {
      this.loading = true;
      try {
        const res = await http.get("sbc/ips");
        const rules = res.data.data.map((rule) => ({
          ...rule,
          offer_codecs: rule.offer_codecs.split(","),
        }));
        this.rules = rules;
      } catch (error) {}
      this.loading = false;
    },
    async deleteHandler() {
      this.deleteLoading = true;
      await http
        .delete(`/sbc/ips/${this.currentIPID}`)
        .then((res) => {
          setTimeout(() => {
            this.$modal.hide("deletePopup");
            this.deleteLoading = false;
            this.fetchRules();
          }, 500);
          this.currentIPID = null;
          this.$notify({
            group: "datatable",
            title: "IP Deleted Successfully!",
            type: "success",
          });
        })
        .catch((err) => {
          this.$notify({
            group: "datatable",
            title: "Something went wrong!",
            type: "error",
          });
          this.deleteLoading = false;
        });
    },
    async uploadFile() {
      this.loading = true;
      await http
        .post("/sbc/ips/restore", JSON.parse(this.importedJSON))
        .then((res) => {
          this.$notify({
            group: "rules",
            title: res.data.message,
            type: "success",
          });
          this.fetchRules();
          this.importedJSON = "";
          this.file = null;
          this.loading = false;
        })
        .catch((err) => {
          this.$notify({
            group: "rules",
            title: err.response.data.message,
            type: "error",
          });
          this.file = null;
          this.importedJSON = "";
          this.loading = false;
        });
    },
    async createIP() {
      this.createLoading = true;
      this.error = {};
      try {
        const offer_codecs = this.form.offer_codecs.join(",");
        const result = await http.post("/sbc/ips", {
          ...this.form,
          offer_codecs,
        });
        this.createLoading = false;
        this.$modal.hide("createIPModal");
        this.clearData();
        this.fetchRules();
        this.$notify({
          group: "rules",
          title: result.data.message,
          type: "success",
        });
        this.error = {};
      } catch (err) {
        this.createLoading = false;
        this.$notify({
          group: "rules",
          title: err.response ? err.response.data.message : err.message,
          type: "error",
        });
        this.error = err.response ? err.response.data.error : err.message;
      }
    },
    async editIP() {
      this.editLoading = true;
      this.editError = {};
      await http
        .put(`/sbc/ips/${this.editForm.id}`, {
          ...this.editForm,
          offer_codecs: Array.isArray(this.editForm.offer_codecs)
            ? this.editForm.offer_codecs.join(",")
            : this.editForm.offer_codecs,
        })
        .then((result) => {
          this.editLoading = false;
          this.fetchRules();
          this.$modal.hide("editIpModal");
          this.$notify({
            group: "rules",
            title: result.data.message,
            type: "success",
          });
        })
        .catch((err) => {
          this.editLoading = false;
          this.$notify({
            group: "rules",
            title: err.response.data.message,
            type: "error",
          });
          this.editError = err.response.data.error;
        });
    },
    async deleteSubnet(idx) {
      this.editForm.ips.splice(idx, 1);
      try {
        await http.delete(`sbc/ips/subnet/${this.ips[idx].id}`);
        this.ips.splice(idx, 1);
      } catch (error) {
        console.log(error);
      } finally {
      }
    },
  },
};
</script>

<style lang="scss">
.btn.btn-info {
  background-color: #469598 !important;
  border-color: #469598 !important;
}
.custom-file-input ~ .custom-file-label[data-browse]::after {
  content: "" !important;
  display: none;
}
.custom-input-label {
  position: absolute;
  z-index: 0;
  color: #fff;
  width: 95px;
  display: flex;
  cursor: pointer;
  background-color: #469598 !important;
  border-color: #469598 !important;
  height: 100%;
  border-radius: 7px;
  align-items: center;
  justify-content: center;
}
.custom-file {
  width: 95px;
  opacity: 0;
  cursor: pointer;
  .custom-file-label {
    background-color: #469598 !important;
    border-color: #469598 !important;
    .form-file-text {
      opacity: 0;
      cursor: pointer;
      pointer-events: all !important;
    }
  }
}
.vm--modal .container {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
